import { useState, useMemo } from "react";
import type { TFunction } from "i18next";
import { Radar } from "iconsax-react";
import { ModalForm, Button, Input, Skeleton, Toast } from "@/shared/ui";
import { getAllocationColor } from "../../../utils";
import type { WorkerAllocationSummary } from "../../../hooks";

interface ProjectOption {
  id: number;
  name: string;
}

interface Props {
  worker: WorkerAllocationSummary;
  onClose: () => void;
  workerProjects: ProjectOption[];
  isLoadingProjects: boolean;
  onSave: (workerId: number, allocations: Array<{ project_id: number; hours: string }>) => Promise<void>;
  isSaving: boolean;
  isDraft: boolean;
  canEdit: boolean;
  t: TFunction;
}

const CIRCUMFERENCE = 2 * Math.PI * 92;

function formatHours(hours: string | number): string {
  const num = typeof hours === "string" ? parseFloat(hours) : hours;
  if (isNaN(num)) return "0";
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.?0+$/, "");
}

export default function WorkerAllocationModal({
  worker,
  onClose,
  workerProjects,
  isLoadingProjects,
  onSave,
  isSaving,
  isDraft,
  canEdit,
  t,
}: Props) {
  const canModify = canEdit && isDraft;

  const [formValues, setFormValues] = useState<Record<number, string>>(() => {
    const values: Record<number, string> = {};
    for (const alloc of worker.allocations) {
      values[alloc.project_id] = formatHours(alloc.hours);
    }
    for (const project of workerProjects) {
      if (!(project.id in values)) {
        values[project.id] = "";
      }
    }
    return values;
  });

  const allProjects = useMemo(() => {
    const projectMap = new Map<number, { id: number; name: string }>();
    for (const project of workerProjects) {
      projectMap.set(project.id, project);
    }
    for (const alloc of worker.allocations) {
      if (!projectMap.has(alloc.project_id)) {
        projectMap.set(alloc.project_id, { id: alloc.project_id, name: alloc.project_name });
      }
    }
    return Array.from(projectMap.values());
  }, [workerProjects, worker.allocations]);

  const totalAllocated = useMemo(() => {
    let sum = 0;
    for (const val of Object.values(formValues)) {
      const num = parseFloat(val);
      if (!isNaN(num) && num > 0) sum += num;
    }
    return sum;
  }, [formValues]);

  const totalPercent = worker.workHours > 0 ? Math.round((totalAllocated / worker.workHours) * 100) : 0;
  const remaining = Math.max(0, worker.workHours - totalAllocated);
  const isOverAllocated = totalAllocated > worker.workHours;

  function handleHoursChange(projectId: number, value: string) {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormValues((prev) => ({ ...prev, [projectId]: value }));
    }
  }

  function handleSave() {
    const items = allProjects.map((p) => ({
      project_id: p.id,
      hours: formValues[p.id] || "0",
    }));
    onSave(worker.workerId, items);
  }

  function buildDonutSegments() {
    if (worker.workHours <= 0) return [];

    const activeProjects = allProjects.filter((p) => parseFloat(formValues[p.id] || "0") > 0);
    const hasRemaining = remaining > 0;
    const segmentCount = activeProjects.length + (hasRemaining ? 1 : 0);

    if (segmentCount === 0) return [];

    const GAP = segmentCount > 1 ? 18 : 0;
    const totalGap = GAP * segmentCount;
    const availableLength = CIRCUMFERENCE - totalGap;

    const segments: { offset: number; length: number; color: string; isGray: boolean }[] = [];
    let consumed = 0;
    let colorIdx = 0;

    for (const project of activeProjects) {
      const hours = parseFloat(formValues[project.id] || "0");
      const fraction = Math.min(hours / worker.workHours, 1);
      const length = Math.max(1, fraction * availableLength);
      segments.push({ offset: CIRCUMFERENCE - consumed, length, color: getAllocationColor(colorIdx), isGray: false });
      consumed += length + GAP;
      colorIdx++;
    }

    if (hasRemaining) {
      const fraction = remaining / worker.workHours;
      const length = Math.max(1, fraction * availableLength);
      segments.push({ offset: CIRCUMFERENCE - consumed, length, color: "", isGray: true });
    }

    return segments;
  }

  const donutSegments = buildDonutSegments();

  function getAllocatedProjectsWithColors() {
    const result: { name: string; hours: number; color: string }[] = [];
    let colorIdx = 0;
    for (const project of allProjects) {
      const hours = parseFloat(formValues[project.id] || "0");
      if (hours <= 0) continue;
      result.push({ name: project.name, hours, color: getAllocationColor(colorIdx) });
      colorIdx++;
    }
    return result;
  }

  const allocatedProjects = getAllocatedProjectsWithColors();

  const computedFooter =
    !isLoadingProjects && canModify ? (
      <div className="grid grid-cols-[2fr_3fr] gap-3">
        <Button variant="secondary" size="huge" onClick={onClose} disabled={isSaving}>
          {t("allocations.modal.cancel")}
        </Button>
        <Button
          variant="primary"
          size="huge"
          onClick={handleSave}
          disabled={isSaving || isOverAllocated}
          fullWidthTooltip
          textDuringDisabled={isOverAllocated ? t("allocations.modal.rules.overAllocated") : undefined}>
          {isSaving ? t("actions.saving") : t("allocations.modal.save")}
        </Button>
      </div>
    ) : undefined;

  return (
    <ModalForm
      icon={Radar}
      iconClassName="background-neutral-fill radial-glow text-white"
      onClose={onClose}
      width="w-[540px]"
      footer={computedFooter}>
      {isLoadingProjects ? (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 mb-5">
            <Skeleton width={200} height={28} />
            <Skeleton width={280} height={16} />
          </div>
          <div className="flex items-center gap-5">
            <div className="flex flex-col gap-2 flex-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} width={140} height={16} />
              ))}
            </div>
            <Skeleton width={204} height={204} borderRadius="50%" />
          </div>
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton width={160} height={16} className="flex-1" />
                <Skeleton width={64} height={32} borderRadius={6} />
                <Skeleton width={48} height={16} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 mb-5">
            <p className="text-display-2xs content-base-primary">{worker.workerName}</p>
            <p className="text-body-regular-md content-base-secondary">{t("allocations.modal.subtitle")}</p>
          </div>

          <div className="flex items-center gap-5">
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              {allocatedProjects.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5 min-w-0">
                  <span className="w-1 h-5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-body-regular-md content-action-neutral truncate">{item.name}</span>
                </div>
              ))}
              {remaining > 0 && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-1 h-5 rounded-full shrink-0 bg-grey-200 dark:bg-grey-700" />
                  <span className="text-body-regular-md content-action-neutral truncate">
                    {t("allocations.modal.unallocated")}
                  </span>
                </div>
              )}
            </div>

            <svg width="204" height="204" viewBox="0 0 204 204" className="shrink-0">
              {donutSegments.length === 0 && (
                <circle
                  cx="102"
                  cy="102"
                  r="92"
                  fill="none"
                  className="stroke-grey-200 dark:stroke-grey-700"
                  strokeWidth="15"
                />
              )}
              {donutSegments.map((seg, i) => (
                <circle
                  key={i}
                  cx="102"
                  cy="102"
                  r="92"
                  fill="none"
                  stroke={seg.isGray ? undefined : seg.color}
                  className={seg.isGray ? "stroke-grey-200 dark:stroke-grey-700" : undefined}
                  strokeWidth="15"
                  strokeDasharray={`${seg.length} ${CIRCUMFERENCE - seg.length}`}
                  strokeDashoffset={seg.offset}
                  transform="rotate(-90 102 102)"
                  strokeLinecap="round"
                />
              ))}
              <text x="102" y="94" textAnchor="middle" dominantBaseline="central">
                <tspan className="text-display-2xs content-base-primary fill-current">
                  {formatHours(totalAllocated)}
                </tspan>
                <tspan className="text-display-2xs content-base-low fill-current">/{formatHours(worker.workHours)}</tspan>
              </text>
              <text
                x="102"
                y="118"
                textAnchor="middle"
                dominantBaseline="central"
                className="text-body-regular-sm content-base-secondary fill-current">
                {t("allocations.modal.hoursLabel")}
              </text>
            </svg>
          </div>

          {canModify && allProjects.length > 0 && (
            <div className="flex flex-col">
              <p className="text-label-md content-base-primary mb-2">{t("allocations.modal.distributeTitle")}</p>
              <div className="flex flex-col gap-2 mb-7">
                {allProjects.map((project) => {
                  const val = formValues[project.id] || "";
                  const hours = parseFloat(val) || 0;
                  const pct = worker.workHours > 0 ? Math.round((hours / worker.workHours) * 100) : 0;
                  return (
                    <div key={project.id} className="flex items-center gap-2">
                      <p className="text-body-regular-md content-action-neutral flex-1 truncate">{project.name}</p>
                      <div className="flex">
                        <Input
                          placeholder="0ч"
                          value={val}
                          onChange={(e) => handleHoursChange(project.id, e.target.value)}
                          disabled={isSaving}
                          isSmall
                          className="w-16!"
                        />
                      </div>
                      <span
                        className={`w-12 text-label-md ${pct > 0 ? "content-base-primary" : "content-base-low"}  text-center shrink-0`}>
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <p className="text-label-md text-right content-base-low flex-1">{t("allocations.modal.remainingRow")}</p>
                  <span
                    className={`w-12 text-label-md text-center shrink-0 ${remaining > 0 ? "content-action-notice" : "content-base-low"}`}>
                    {formatHours(remaining)}{t("allocations.table.hoursShort")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-label-md text-right content-base-low flex-1">{t("allocations.modal.totalRow")}</p>
                  <span
                    className={`w-12 text-label-md text-center shrink-0 ${isOverAllocated ? "content-action-negative" : totalPercent === 0 ? "content-base-low" : "content-base-primary"}`}>
                    {totalPercent}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {canModify && allProjects.length === 0 && (
            <Toast
              color="notice"
              text={t("allocations.noProjectsWarning")}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}
        </div>
      )}
    </ModalForm>
  );
}

